// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { BillingPlanListItem } from '@/billing/api/types';
import { formatNumber } from '@/utilities/number';
import { Box, Stack, Typography } from '@mui/material';

interface Props {
  plan: BillingPlanListItem;
}

// Four-cell mini stat box: Planned / Billable / On Invoice / Cancelled.
// Each cell renders its label first, count second — matches the verbal
// shape ("12 planned charges") rather than abbreviating to numerals.
// No tooltip; the labels are spelled out so the cell is self-describing.
export function BillingPlanChargesStatBox(props: Props) {
  const { plan } = props;
  return (
    <Stack
      direction="row"
      sx={{
        display: 'inline-flex',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <StatCell
        label="Planned"
        count={plan.plannedCount}
        color="text.secondary"
      />
      <StatCell
        label="Billable"
        count={plan.billableCount}
        color="primary.main"
      />
      <StatCell
        label="On invoice"
        count={plan.invoicedCount}
        color="success.main"
      />
      <StatCell
        label="Cancelled"
        count={plan.cancelledCount}
        color="error.main"
      />
    </Stack>
  );
}

function StatCell(props: { label: string; count: number; color: string }) {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.5,
        textAlign: 'center',
        minWidth: 56,
        borderLeft: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderLeft: 0 },
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="body2" sx={{ color: props.color, fontWeight: 600 }}>
        {formatNumber(props.count)}
      </Typography>
    </Box>
  );
}
